import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TopPanelComponent from "./TopPanelComponent";
import FiltersComponent from "./Filters/FiltersComponent";
import PolicyTableComponent from "./PolicyTable/PolicyTableComponent";
import { initializeWorkspaceController } from "../../utils/WorkspaceUtils";
import { useWorkspace } from "context/WorkspaceContext";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import {
  WORKSPACE_CLOB_KEY,
  WORKSPACE_STATUS_TEXT_UPDATE,
} from "constants/Constants";
import WorkspaceController from "controller/WorkspaceController";
import { consolidateRules, deConsolidateRules } from "utils/workSpaceFilter";
import Api from "api";
import { Box } from "@mui/material";
import { decompressFromUTF16 } from "lz-string";
import { postUserActivity } from "components/shared/postUserActivity";
import { WORKFLOW_CODING } from "constants/Constants";
import { isClobValid } from "utils/shared";
import { validateAndStringifyClob } from "utils/clobValidation";

function getPolicyState(state) {
  if (state == null || Object.keys(state).length === 0) {
    state = JSON.parse(sessionStorage.getItem("policyState"));
  } else {
    sessionStorage.setItem("codingState", JSON.stringify(state));
  }

  return state;
}

const rowValidation = (rule) => {
  if (!rule.editRule || rule.editRule === "EDIT") {
    return rule;
  }

  const errors = {};
  // If dxCodes or dxCodesExpanded is empty, show error
  if (
    rule.codingClassification === "paNotReqd" &&
    (!rule.dxCodes ||
      !rule.dxCodes.dxCodesExpanded ||
      typeof rule.dxCodes.dxCodesExpanded !== "string" ||
      rule.dxCodes.dxCodesExpanded.trim() === "")
  ) {
    errors.dxCodes = "DX Code(s) Required";
  }

  if (!rule.codingClassification) {
    errors.classification = "Classification Required";
  }
  if (!rule.ccNumber) {
    errors.cc = "CC# Required";
  }
  // Return a new object with errors
  return { ...rule, errors };
};

const ALL = "ALL";

const PolicyCoding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [activityUpdates, setActivityUpdates] = useState([]);
  const [filteredRows, setFilteredRows] = useState(rows);
  const [showErrors, setshowErrors] = useState(false);
  const policyState = getPolicyState(location?.state?.policyState || {});
  const { policyNumber } = location.state;
  const { policyTitle } = location.state;
  const { policyVersion } = location.state;
  const editable = policyState.editMode || false;
  const [validationFilter, setValidationFilter] = useState(ALL);

  const healthPlanDisplayName =
    policyState?.healthPlanDisplayName || "[Health Plan]";

  const { workspaceController, setWorkspaceController } = useWorkspace();
  const [workspaceLoaded, setWorkspaceLoaded] = useState(false);
  const [policyHeader, setPolicyHeader] = useState({});
  const topPanelRef = useRef(null);
  const filtersRef = useRef(null);
  const [policyTabsHeight, setPolicyTabsHeight] = useState("auto");

  const hasRunEffect = useRef(false);
  const originalWorkspace = useMemo(() => {
    const storedWorkspace = localStorage.getItem(WORKSPACE_CLOB_KEY);
    if (!storedWorkspace) {
      console.warn("No workspace found in localStorage");
      return null;
    }
    const decompressed = decompressFromUTF16(storedWorkspace);
    const workspace = JSON.parse(decompressed);
    return new WorkspaceController(null, null, null, null, workspace);
  }, []);

  useEffect(() => {
    if (!hasRunEffect.current && workspaceController?.clob) {
      workspaceController.setClob(consolidateRules(workspaceController.clob));

      hasRunEffect.current = true;
    }
  }, [workspaceController?.clob, hasRunEffect.current]);

  const handleAccordionToggle = () => {
    setTimeout(() => {
      calculatePolicyTabsHeight();
    }, 400);
  };

  const calculatePolicyTabsHeight = () => {
    if (!filtersRef.current) {
      setTimeout(calculatePolicyTabsHeight, 200);
      return;
    }
    const topPanelHeight = topPanelRef.current
      ? topPanelRef.current.offsetHeight
      : 0;
    const filtersHeight = filtersRef.current
      ? filtersRef.current.offsetHeight
      : 0;
    const viewportHeight = window.innerHeight;

    setPolicyTabsHeight(viewportHeight - topPanelHeight - filtersHeight);
  };

  useLayoutEffect(() => {
    calculatePolicyTabsHeight(); // Initial calculation
    window.addEventListener("resize", calculatePolicyTabsHeight);
    return () => {
      window.removeEventListener("resize", calculatePolicyTabsHeight);
    };
  }, []);

  // policy header stays blank all time.
  useEffect(() => {
    initializeWorkspaceController(
      workspaceController,
      setWorkspaceController,
      setWorkspaceLoaded
    );
  }, [workspaceController, setWorkspaceController]);

  const handleCancelAction = () => {
    setWorkspaceController(null);
    navigate(-1);
  };

  useEffect(() => {
    if (workspaceLoaded && workspaceController != null) {
      const rowArr = [];
      const policyRulesList = workspaceController.getClobPolicyRulesList();
      policyRulesList
        .filter(
          (p) =>
            p.RuleHeader.EditRule !== "DELETE" &&
            p.RuleHeader.EditRule !== "EDIT" &&
            !p.RuleFacts?.RuleEndDate?.length
        )
        .forEach((p, i) => {
          const rulesFacts = p.RuleFacts;

          //Push data in to the table.
          rowArr.push({
            id: i,
            code: rulesFacts.ProcedureCode,
            description: rulesFacts.ProcedureCodeDescription,
            ccNumber: rulesFacts.CoverageCriteriaList,
            codingClassification: rulesFacts.CodingClassification,
            dxCodes: {
              dxCodesExpanded: rulesFacts.DiagnosisListExpanded || "",
              dxCodesCondensed: rulesFacts?.DiagnosisListCondensed || "",
            },
            zCodes: rulesFacts.ZCodeList,
            paaQuestions: rulesFacts.PAAQuestions,
            actions: "Edit/Delete",
            index: p.index,
            editRule: p.RuleHeader?.EditRule,
            tempIdList: p?.tempIdList?.length + "||" + p?.tempIdList?.join(","),
            tempId: p.tempId,
            ruleVersion: p.RuleHeader?.RuleVersion,
          });
        });
      setRows(rowArr.map(rowValidation));
    }
  }, [workspaceController, workspaceLoaded]);
  const errorCount = useMemo(
    () => rows.filter((r) => r.errors && Object.keys(r.errors).length).length,
    [rows]
  );

  const handleValidate = (policyState, ctReviewData, submit) => {
    if (
      submit &&
      policyState.policyWorkflowTaskCategory?.toLowerCase() === "coding" &&
      (policyState.policyWorkflowTaskStatus?.toLowerCase() === "peer review" ||
        policyState.policyWorkflowTaskStatus?.toLowerCase() === "pdc review")
    ) {
      if (!ctReviewData || !ctReviewData.reviewType || !ctReviewData.results) {
        dispatch(setDialog({ message: "C&T Review is required" }));
        return false; // validation failed
      }
    }
    if (errorCount) {
      setshowErrors(true);
      setValidationFilter("FAILED_VALIDATION");
      return false; // validation failed
    }

    if (filteredRows.length === 0) {
      return true;
    }

    dispatch(setDialog({ message: "Validation Passed" }));
    return true; // validation passed
  };
  if (!workspaceLoaded || !workspaceController) {
    return <div>Loading...</div>;
  }

  const updatePolicyData = async (
    policyState,
    submit,
    workspaceController,
    policyHistoryData,
    ctReviewData,
    updateAssignessReviewerDto,
    policyChangeControlXrefDto
  ) => {
    workspaceController.updateChangeControlForAllRules(
      policyChangeControlXrefDto?.changeControlText
    );
    let updatedWorkspace = workspaceController.workspace;
    updatedWorkspace.policyWorkflowTaskCategory =
      policyState?.policyWorkflowTaskCategory;
    updatedWorkspace.policyWorkflowTaskStatus =
      policyState.policyWorkflowTaskStatus;
    updatedWorkspace.workspaceClob = {
      ...workspaceController.clob,
      PolicyBusinessRulesList: deConsolidateRules(
        originalWorkspace?.clob?.PolicyBusinessRulesList ?? [],
        workspaceController?.clob?.PolicyBusinessRulesList ?? []
      ),
    };

    // Validate + stringify in one place
    const clobString = validateAndStringifyClob(
      updatedWorkspace.workspaceClob,
      dispatch,
      false
    );
    if (!clobString) return; // validation failed, dialog already shown

    updatedWorkspace.workspaceStatusText = WORKSPACE_STATUS_TEXT_UPDATE;
    if (!isClobValid(updatedWorkspace, dispatch)) return; // validation failed, exit early
    updatedWorkspace.workspaceClob = clobString;
    dispatch(setLoading(true));

    try {
      const response = await Api.workspace.updateWorkSpceClob(
        updatedWorkspace,
        submit,
        policyHistoryData,
        ctReviewData,
        updateAssignessReviewerDto,
        policyChangeControlXrefDto
      );

      if (response.status === 200) {
        localStorage.removeItem(WORKSPACE_CLOB_KEY);
        try {
          const update = submit ? "Submit" : "Update";
          await postUserActivity(
            workspaceController?.workspace?.healthPlanKey,
            workspaceController?.workspace?.policyKey,
            response.status,
            WORKFLOW_CODING,
            "click",
            "updatePolicyData",
            update,
            "Policy Coding",
            activityUpdates
          );
        } catch (error) {
          console.log("Failed to post user activity:", error);
          dispatch(
            setDialog({
              title: "Error",
              message: error.message || "Failed to post user activity",
            })
          );
        }
        navigate(-1);
      }
    } catch (e) {
      dispatch(setDialog({ message: e.message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div ref={topPanelRef}>
        <TopPanelComponent
          policyStateData={policyState}
          policyHeader={policyHeader}
          policyKey={workspaceController?.workspace?.policyKey}
          healthPlanDisplayName={healthPlanDisplayName}
          healthPlanKey={workspaceController?.workspace?.healthPlanKey}
          updatePolicyData={(
            submit,
            policyHistoryData,
            ctReviewData,
            updateAssignessReviewerDto,
            policyChangeControlXrefDto
          ) => {
            // If validation fails, short-circuit (return immediately).
            if (!handleValidate(policyState, ctReviewData, submit)) {
              return;
            }
            if (!errorCount)
              updatePolicyData(
                policyState,
                submit,
                workspaceController,
                policyHistoryData,
                ctReviewData,
                updateAssignessReviewerDto,
                policyChangeControlXrefDto
              );
          }}
          handleCancelAction={handleCancelAction}
          policyNumber={policyNumber}
          policyTitle={policyTitle}
          policyVersion={policyVersion}
          editable={editable}
        />
      </div>
      <Box ref={filtersRef} my={1}>
        <FiltersComponent
          selectedHealthPlan={policyState?.healthPlanName}
          onAccordionToggle={handleAccordionToggle}
          rows={rows}
          setFilteredRows={setFilteredRows}
          setshowErrors={setshowErrors}
          validationFilter={validationFilter}
          setValidationFilter={setValidationFilter}
        />
      </Box>
      <div style={{ overflow: "hidden", paddingTop: "5px" }}>
        <PolicyTableComponent
          workspaceController={workspaceController}
          setWorkspaceController={setWorkspaceController}
          editable={editable}
          gridHeight={policyTabsHeight}
          filteredRows={filteredRows}
          rowsData={rows}
          showValidationErrors={showErrors}
          handleValidate={handleValidate}
          policyNumber={policyNumber}
          activityUpdates={activityUpdates}
          setActivityUpdates={setActivityUpdates}
        />
      </div>
    </div>
  );
};

export default PolicyCoding;
